import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CenteredMetricStepper from '../ui/CenteredMetricStepper'
import MetricPreviewCard from '../ui/MetricPreviewCard'
import MaterialIcon from '../ui/MaterialIcon'
import CyclePhaseRing from './CyclePhaseRing'
import { KILO } from '../../constants/KILO_THEME'
import {
  daysUntilNextPeriod,
  getCycleDay,
  getCyclePhase,
  type CyclePhase,
  type CycleProfile,
} from '../../lib/cycle'

export type CyclePanelStep = 'overview' | 'cycleLength' | 'periodDays'

type CycleTrackingPanelProps = {
  profile: CycleProfile
  step: CyclePanelStep
  onStepChange: (step: CyclePanelStep) => void
  onSave: (fields: {
    last_period_start_date: string | null
    avg_cycle_length: number
    avg_period_duration: number
  }) => Promise<void>
}

const PHASE_ACCENT: Record<CyclePhase, string> = {
  menstrual:
    'border-pink-300 bg-pink-50 text-pink-950 dark:border-pink-400/40 dark:bg-pink-950/30 dark:text-pink-100',
  follicular:
    'border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-400/40 dark:bg-emerald-950/30 dark:text-emerald-100',
  ovulatory:
    'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-400/40 dark:bg-amber-950/30 dark:text-amber-100',
  luteal:
    'border-orange-300 bg-orange-50 text-orange-950 dark:border-orange-400/40 dark:bg-orange-950/30 dark:text-orange-100',
}

const CycleTrackingPanel = ({ profile, step, onStepChange, onSave }: CycleTrackingPanelProps) => {
  const { t } = useTranslation()
  const [startDate, setStartDate] = useState(profile.last_period_start_date ?? '')
  const [cycleLength, setCycleLength] = useState(profile.avg_cycle_length)
  const [periodDuration, setPeriodDuration] = useState(profile.avg_period_duration)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setStartDate(profile.last_period_start_date ?? '')
    setCycleLength(profile.avg_cycle_length)
    setPeriodDuration(profile.avg_period_duration)
  }, [profile.last_period_start_date, profile.avg_cycle_length, profile.avg_period_duration])

  const draftProfile: CycleProfile = useMemo(
    () => ({
      ...profile,
      last_period_start_date: startDate || null,
      avg_cycle_length: cycleLength,
      avg_period_duration: periodDuration,
    }),
    [profile, startDate, cycleLength, periodDuration],
  )

  const phase = getCyclePhase(draftProfile)
  const daysUntil = daysUntilNextPeriod(draftProfile)
  const dirty =
    startDate !== (profile.last_period_start_date ?? '') ||
    cycleLength !== profile.avg_cycle_length ||
    periodDuration !== profile.avg_period_duration

  const handleSave = async () => {
    setSaving(true)
    await onSave({
      last_period_start_date: startDate || null,
      avg_cycle_length: cycleLength,
      avg_period_duration: periodDuration,
    })
    setSaving(false)
  }

  if (step === 'cycleLength') {
    return (
      <CenteredMetricStepper
        label={t('app.profile.cycleLength')}
        value={cycleLength}
        unit={t('app.profile.daysUnit')}
        icon="calendar_month"
        min={21}
        max={40}
        onSave={async (value) => {
          setCycleLength(value)
          onStepChange('overview')
        }}
      />
    )
  }

  if (step === 'periodDays') {
    return (
      <CenteredMetricStepper
        label={t('app.profile.periodDays')}
        value={periodDuration}
        unit={t('app.profile.daysUnit')}
        icon="water_drop"
        min={3}
        max={8}
        onSave={async (value) => {
          setPeriodDuration(value)
          onStepChange('overview')
        }}
      />
    )
  }

  return (
    <div className="space-y-5">
      <CyclePhaseRing profile={draftProfile} />

      {phase ? (
        <div className={`rounded-2xl border px-4 py-3 ${PHASE_ACCENT[phase]}`}>
          <p className="text-xs font-semibold uppercase tracking-widest opacity-70 dark:opacity-80">
            {t('app.profile.currentPhaseLabel')}
          </p>
          <p className="mt-1 text-base font-semibold">{t(`app.profile.phases.${phase}`)}</p>
          {daysUntil != null ? (
            <p className="mt-1 text-xs opacity-70 dark:opacity-80">{t('app.profile.daysUntilPeriod', { count: daysUntil })}</p>
          ) : null}
        </div>
      ) : null}

      <div className="rounded-2xl border border-kilo-border bg-kilo-card p-4">
        <p className={`text-sm leading-relaxed ${KILO.text.muted}`}>{t('app.profile.cycleGuidanceIntro')}</p>
        {phase ? (
          <p className={`mt-2 text-sm leading-relaxed ${KILO.text.primary}`}>
            {t(`app.profile.phaseGuidance.${phase}`)}
          </p>
        ) : null}
      </div>

      <div className="space-y-3">
        <p className={KILO.meta}>{t('app.profile.cycleSettings')}</p>

        <label className="block space-y-2">
          <span className={KILO.fieldLabel}>{t('app.profile.lastPeriodStart')}</span>
          <input
            type="date"
            value={startDate}
            max={new Date().toISOString().split('T')[0]}
            onChange={(e) => setStartDate(e.target.value)}
            className={`h-12 w-full ${KILO.field} border-kilo-danger-border/50 focus:border-kilo-cycle/50`}
          />
          <div className="flex flex-wrap gap-2">
            {[7, 14, 21, 28].map((daysAgo) => {
              const d = new Date()
              d.setDate(d.getDate() - daysAgo)
              const iso = d.toISOString().split('T')[0]
              return (
                <button
                  key={daysAgo}
                  type="button"
                  onClick={() => setStartDate(iso)}
                        className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                          startDate === iso
                            ? 'border-rose-400 bg-rose-50 text-rose-900 dark:border-kilo-cycle/50 dark:bg-kilo-cycle/10 dark:text-kilo-cycle'
                            : 'border-kilo-border text-kilo-muted hover:border-kilo-accent/30'
                        }`}
                >
                  {t('app.profile.periodQuickPick', { days: daysAgo })}
                </button>
              )
            })}
          </div>
        </label>

        <div className="grid grid-cols-2 items-stretch gap-2">
          <MetricPreviewCard
            label={t('app.profile.cycleLength')}
            value={cycleLength}
            unit={t('app.profile.daysUnit')}
            icon="calendar_month"
            onClick={() => onStepChange('cycleLength')}
          />
          <MetricPreviewCard
            label={t('app.profile.periodDays')}
            value={periodDuration}
            unit={t('app.profile.daysUnit')}
            icon="water_drop"
            onClick={() => onStepChange('periodDays')}
          />
        </div>
      </div>

      <div className="rounded-2xl border border-kilo-border/60 bg-kilo-nested/30 p-4">
        <div className="mb-2 flex items-center gap-2">
          <MaterialIcon name="menu_book" size="sm" className="text-kilo-accent" />
          <p className={KILO.meta}>{t('app.profile.cycleEvidenceTitle')}</p>
        </div>
        <ul className={`space-y-2 text-xs leading-relaxed ${KILO.text.soft}`}>
          <li>{t('app.profile.cycleEvidenceMcNulty')}</li>
          <li>{t('app.profile.cycleEvidenceIssn')}</li>
          <li>{t('app.profile.cycleEvidenceReds')}</li>
        </ul>
        <p className={`mt-3 text-[10px] leading-relaxed ${KILO.text.soft}`}>{t('app.profile.cycleDisclaimer')}</p>
      </div>

      {dirty ? (
        <button
          type="button"
          disabled={saving}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-kilo-accent text-sm font-bold text-black disabled:opacity-50"
          onClick={() => void handleSave()}
        >
          <MaterialIcon name="check" size="sm" />
          {saving ? t('app.profile.saving') : t('app.profile.logCycleData')}
        </button>
      ) : null}
    </div>
  )
}

type CycleTrackingTeaserProps = {
  profile: CycleProfile
  onOpen: () => void
}

export const CycleTrackingTeaser = ({ profile, onOpen }: CycleTrackingTeaserProps) => {
  const { t } = useTranslation()
  const savedPhase = getCyclePhase(profile)
  const savedDay = getCycleDay(profile)

  const teaserLine =
    savedPhase && savedDay != null
      ? t('app.profile.cycleTeaserSummary', {
          phase: t(`app.profile.phasesShort.${savedPhase}`),
          day: savedDay,
          length: profile.avg_cycle_length,
        })
      : t('app.profile.cycleTeaserEmpty')

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full rounded-xl border border-kilo-danger-border bg-kilo-danger-bg/60 p-4 text-left transition-colors hover:border-kilo-cycle/50 active:bg-kilo-danger-bg dark:border-kilo-danger-border/50 dark:bg-kilo-danger-bg/20 dark:hover:border-kilo-cycle/40"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <MaterialIcon name="calendar_month" filled size="sm" className="text-rose-600 dark:text-kilo-cycle" />
            <p className={`${KILO.meta} !text-rose-800 dark:!text-kilo-cycle`}>{t('app.profile.cycleTracking')}</p>
          </div>
          <p className={`mt-2 text-sm ${KILO.text.muted}`}>{teaserLine}</p>
        </div>
        <MaterialIcon name="chevron_right" size="sm" className="shrink-0 text-kilo-soft" />
      </div>
    </button>
  )
}

export default CycleTrackingPanel
